import { createI18n } from 'vue-i18n'
import { SUPPORTED_LOCALES, messages, PLURAL_RULES, LOCALE_ENDONYMS, type AppLocale } from './messages'

export type { AppLocale }
export { SUPPORTED_LOCALES, messages, PLURAL_RULES, LOCALE_ENDONYMS }

const STORAGE_KEY = 'uiLocale'
export const DEFAULT_LOCALE: AppLocale = 'pl'

function isValidLocale(value: string | null): value is AppLocale {
  return value !== null && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

export function loadStoredLocale(): AppLocale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isValidLocale(stored)) return stored
  } catch {
    // storage unavailable (tests, SSR) — fall back to default
  }
  return DEFAULT_LOCALE
}

export function saveLocaleToStorage(locale: AppLocale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // ignore
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale
  }
}

const i18n = createI18n({
  legacy: false,
  locale: loadStoredLocale(),
  fallbackLocale: 'en',
  messages,
  pluralRules: PLURAL_RULES,
})

// Keep <html lang> in sync on first load.
saveLocaleToStorage(i18n.global.locale.value)

export function setLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale
  saveLocaleToStorage(locale)
}

/**
 * Simple-typed translator for non-component code (stores, utilities).
 * The overloaded generic typing of `i18n.global.t` must not be aliased in
 * other modules — it trips TS2589 (excessively deep instantiation).
 */
export function translate(key: string, params?: Record<string, unknown>): string {
  // `i18n.global.t` is an overloaded generic whose type is too deep for the
  // checker (TS2589) once the instance is exported. Alias the composer with a
  // simple signature instead.
  const composer = i18n.global as unknown as {
    t: (key: string, params?: Record<string, unknown>) => string
  }
  return composer.t(key, params)
}

export default i18n
